// src/components/Widgets/MaterialDetailsWidget.jsx
import React, { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMaterials } from "../../contexts/MaterialContext";
import MaterialModal from "../Modals/MaterialModal";
import WidgetHeader from "../Common/WidgetHeader";
import { navigateTo, ROUTES } from "../../lib/nav";

const money = (v, currency = "AUD", locale = "en-AU") => {
  const n = Number(v || 0);
  try { return new Intl.NumberFormat(locale, { style: "currency", currency }).format(n); }
  catch { return `${currency} ${n.toFixed(2)}`; }
};

const getQty = (m) => Number(m?.quantity ?? m?.stock ?? m?.stockQty ?? m?.startQuantity ?? 0);

export default function MaterialDetailsWidget({ backTo = "/dashboard", settings }) {
  const navigate = useNavigate();
  const { materials = [], addMaterial, updateMaterial } = useMaterials();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const currency = settings?.currency || "AUD";
  const locale = settings?.locale || "en-AU";

  const isLow = (m) =>
    m?.lowStockLimit != null && getQty(m) <= Number(m.lowStockLimit);

  const recent = useMemo(() => {
    const arr = Array.isArray(materials) ? [...materials] : [];
    arr.sort(
      (a, b) =>
        new Date(b?.updatedAt || b?.createdAt || 0) -
        new Date(a?.updatedAt || a?.createdAt || 0)
    );
    return arr.slice(0, 5);
  }, [materials]);

  const onAddNew = () => {
    setEditing(null);
    setOpen(true);
  };

  const onSelect = (mat) => {
    setEditing(mat);
    setOpen(true);
  };

  // Accept optional meta from the modal: { addAnother: boolean }
  const handleSave = (data, meta = {}) => {
    if (editing?.id) {
      updateMaterial(editing.id, data); // (id, patch)
    } else {
      const payload = { id: data.id || Date.now().toString(), ...data };
      addMaterial(payload);
    }

    if (meta.addAnother) {
      setEditing(null);
      setOpen(false);
      requestAnimationFrame(() => setOpen(true));
      return;
    }

    setOpen(false);
    setEditing(null);
  };

  // Create object URL once per File and revoke after load to avoid leaks
  const renderThumb = useCallback((image) => {
    if (!image) {
      return <div className="h-12 w-12 rounded bg-black/10 dark:bg-white/10 flex-shrink-0" />;
    }
    if (typeof image === "string") {
      return (
        <img
          src={image}
          alt=""
          className="h-12 w-12 object-cover rounded border border-black/10 dark:border-white/10 flex-shrink-0"
          onError={(e) => (e.currentTarget.style.display = "none")}
          loading="lazy"
        />
      );
    }
    const url = URL.createObjectURL(image);
    return (
      <img
        src={url}
        alt=""
        className="h-12 w-12 object-cover rounded border border-black/10 dark:border-white/10 flex-shrink-0"
        onLoad={(e) => URL.revokeObjectURL(e.currentTarget.src)}
        onError={(e) => {
          URL.revokeObjectURL(e.currentTarget.src);
          e.currentTarget.style.display = "none";
        }}
      />
    );
  }, []);

  return (
    <div className="rounded-xl p-4 h-full flex flex-col bg-white/60 border border-black/10 backdrop-blur-md text-black shadow
                    dark:bg-white/5 dark:border-white/10 dark:text-white">
      <WidgetHeader
        title={<span className="font-semibold" style={{ color: 'var(--color-primary)' }}>Materials</span>}
        help={
          "Track your materials, stock, and unit costs. “+ New” adds a material; tap a row to edit and view details."
        }
        to={backTo}
        right={
          <div className="flex gap-2">
            <button
              onClick={onAddNew}
              className="px-3 py-1 rounded text-white text-sm transition-colors hover:opacity-90"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              + New
            </button>
            <button
              onClick={() => navigateTo(navigate, ROUTES.MATERIALS, { source: 'MaterialDetailsWidget:viewAll' })}
              className="px-3 py-1 rounded border text-sm border-black/10 dark:border-white/10"
            >
              View All
            </button>
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto space-y-3 mt-2">
        {recent.length > 0 ? (
          recent.map((mat) => {
            const conv = Number(mat.conversionAmount) || 0;
            const purchaseCost = Number(mat.purchaseCost) || 0;
            const unitCost = conv > 0 ? purchaseCost / conv : null;
            const qty = getQty(mat);

            return (
              <button
                key={mat.id || `${mat.name}-${mat.sku}`}
                onClick={() => onSelect(mat)}
                className="w-full text-left rounded-lg p-3 border border-black/10 dark:border-white/10
                           bg-white/70 dark:bg-white/5 shadow-sm hover:shadow transition
                           flex items-center justify-between"
                title="Edit material"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {renderThumb(mat.image)}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-sm truncate" title={mat.name || "Untitled"}>
                        {mat.name || "Untitled"}
                      </h4>
                      {isLow(mat) && (
                        <span
                          className="inline-block text-[10px] leading-none px-2 py-1 rounded-full
                                     border border-red-200 bg-red-50 text-red-700
                                     dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
                          title="Low stock"
                        >
                          Low
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-black/60 dark:text-white/60 truncate">
                      SKU: {mat.sku || "N/A"} — {mat.category || "—"}
                    </p>

                    <p
                      className={`text-xs ${
                        isLow(mat)
                          ? "text-red-700 dark:text-red-300 font-medium"
                          : "text-black/50 dark:text-white/50"
                      }`}
                    >
                      {qty} {mat.trackingUnit || "units"} in stock
                    </p>
                  </div>
                </div>

                <div className="text-right text-sm flex-shrink-0">
                  {unitCost != null && Number.isFinite(unitCost) ? (
                    <>
                      <p className="text-black dark:text-white tabular-nums">
                        {money(unitCost, currency, locale)}
                      </p>
                      <p className="text-xs text-black/50 dark:text-white/50">
                        per {mat.trackingUnit || "unit"}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-black/50 dark:text-white/50">–</p>
                  )}
                </div>
              </button>
            );
          })
        ) : (
          <div className="rounded-lg border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 p-4">
            <p className="text-sm text-black/70 dark:text-white/70">
              No materials yet. Click <span className="font-semibold">+ New</span> to add your first material.
            </p>
          </div>
        )}
      </div>

      {open && (
        <MaterialModal
          initialMaterial={editing}
          onClose={() => {
            setOpen(false);
            setEditing(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
